import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Request, RequestNote } from '../../../core/store/models';
import * as requestActions from '../../../core/store/requests/request.actions';
import { requestSelectors, rootSelectors } from '../../../core/store/selectors';
import { ActionStatus, RequestAndNotes, SavingMessage, Severity } from '../../../core/store/types';
import { getDateToSave } from '../../../core/store/utils';
import { Moment, sosMoment } from '../../../shared/services/moment.service';
import { PDropdownOption } from '../../interfaces/p-dropdown-options';
import { ActionStateService } from '../../services/action-state.service';

export interface AddToRequestModalData {
    assetIds: number[];
    clearSelectedAssets: () => void;
    studioId: number;
}

@Component({
    templateUrl: './add-to-request-modal.component.html',
    styleUrls: ['./add-to-request-modal.component.scss'],
    standalone: false
})
export class AddToRequestModalComponent implements OnInit {
    assetIds: number[];
    clearSelectedAssets: () => void;
    deadline: Moment | Date;
    isNewRequest = true;
    request: Request;
    requestNote: RequestNote;
    requestOptions$: Observable<PDropdownOption[]>;
    requests$: Observable<RequestAndNotes[]>;
    studioId: number;
    isSendRequest = false;
    send = false;
    saveDisabled = false;
    savingMessage: SavingMessage[] = [];

    private moment: Moment;

    constructor(@Inject(MAT_DIALOG_DATA) private data: AddToRequestModalData, public deviceService: DeviceDetectorService, public dialogRef: MatDialogRef<AddToRequestModalComponent>, private store: Store<rootSelectors.State>, private actionStateService: ActionStateService) { }

    ngOnInit() {
        this.moment = sosMoment;
        this.assetIds = this.data.assetIds;
        this.studioId = this.data.studioId;
        this.isNewRequest = true;
        this.request = new Request({ studio_id: this.studioId });
        this.requestNote = new RequestNote({ note: '' });
        this.clearSelectedAssets = this.data.clearSelectedAssets;

        this.store.dispatch(new requestActions.ListUnsentRequestsAction(this.studioId));
        this.requests$ = this.store.select(requestSelectors.getUnsentRequests);
        this.requestOptions$ = this.store.select(requestSelectors.getUnsentRequestsDropdownOptions);
    }

    createNewRequest() {
        this.isNewRequest = true;
        this.request = new Request({ studio_id: this.studioId });
        this.requestNote = new RequestNote({ note: '' });
    }

    addToExistingRequest() {
        this.isNewRequest = false;
        this.isSendRequest = false;
    }

    selectExistingRequest(event) {
        this.requests$.pipe(take(1)).subscribe(requestAndNotes => {
            const requestAndNote = requestAndNotes.find(r => r.request.id === event.value);
            this.request = new Request(requestAndNote.request);
            this.deadline = requestAndNote.request.deadline ? this.moment.utc(requestAndNote.request.deadline).toDate() : null;
            this.requestNote = new RequestNote(requestAndNote.requestNotes[0]);
        });
    }

    save() {
        this.saveDisabled = true;
        this.savingMessage = [{ severity: Severity.Info, summary: 'Saving...' }];
        this.request.deadline = getDateToSave(this.deadline);
        if (this.isNewRequest) {
            this.store.dispatch(new requestActions.AddAction({ request: this.request, note: this.requestNote.note, assetIds: this.assetIds, send: this.send }));
        } else {
            this.store.dispatch(new requestActions.UpdateAction({ request: this.request, requestNote: this.requestNote, assetIds: this.assetIds }));
        }
        this.store.select(requestSelectors.getRequestSaving).pipe(
            filter(rs => rs.status !== ActionStatus.Loading),
            take(1))
            .subscribe(rs => {
                this.saveDisabled = false;
                this.savingMessage = [];
                if (this.send && this.isNewRequest) {
                    this.actionStateService.handle(rs, this.isNewRequest && this.send ? 'Request has been sent.' : '', `Can't send request`);
                } else {
                    this.actionStateService.handle(rs, this.isNewRequest ? 'Request has been created.' : 'Request has been updated.', `Can't Add to Request`);
                }
                this.dialogRef.close();
            });
    }

    removeDate() {
        this.deadline = null;
    }

    onChange(event) {
        this.isSendRequest = event.checked;
        this.send = event.checked;
    }
}
